import React, { useState } from 'react';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { X, Ban, AlertTriangle } from 'lucide-react';

type CancelInvoiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  invoiceId: number;
  invoiceNumber: string;
};

export const CancelInvoiceModal: React.FC<CancelInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  invoiceId,
  invoiceNumber
}) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      toast({
        title: 'Raison requise',
        description: 'Vous devez fournir une raison pour annuler cette facture.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setBusy(true);
      await apiService.patch(`/invoices/${invoiceId}/cancel`, {
        cancellation_reason: reason.trim()
      });

      toast({
        title: 'Facture annulée',
        description: 'La facture a été annulée avec succès.'
      });
      
      onSuccess();
      onClose();
      setReason('');
    } catch (e: any) {
      toast({
        title: 'Erreur',
        description: e?.response?.data?.detail || 'Annulation impossible.',
        variant: 'destructive'
      });
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (!busy) {
      setReason('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Ban className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                Annuler la facture
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {invoiceNumber}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={busy}
            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Warning Box */}
          <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-1">
                  Attention
                </h4>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  L'annulation d'une facture est une action importante. Cette facture ne sera plus comptabilisée 
                  dans le cumul des paiements du devis. La raison d'annulation sera enregistrée pour traçabilité.
                </p>
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              Raison de l'annulation <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              maxLength={1000}
              rows={4}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              placeholder="Ex: Erreur de saisie, montant incorrect, client a annulé la commande..."
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {reason.length}/1000 caractères • Cette raison sera visible dans l'historique
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={busy}
            className="hover:bg-slate-200 dark:hover:bg-slate-700"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={busy || !reason.trim()}
            className="bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Annulation...
              </>
            ) : (
              <>
                <Ban className="h-4 w-4 mr-2" />
                Confirmer l'annulation
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
